import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBrand } from '../../contexts/BrandContext';
import { 
  Calendar, 
  Users, 
  TrendingUp, 
  Award,
  Globe,
  Shield,
  Star,
  ArrowRight
} from 'lucide-react';

const AboutSection = ({ language, onRegister }) => {
  const { tt } = useLanguage();
  const { brandName } = useBrand();
  
  const translations = {
    ru: {
      title: 'Более 18 лет успешной работы',
      subtitle: `${brandName} — надежный партнер для ваших инвестиций с 2006 года`,
      description: 'За годы работы мы помогли десяткам тысяч клиентов достичь финансовых целей через умные инвестиции и профессиональную торговлю.',
      
      milestones: [
        {
          year: '2006',
          title: 'Основание компании',
          description: 'Начало пути в финансовых технологиях'
        },
        {
          year: '2010',
          title: '10,000+ клиентов',
          description: 'Достигли первого значимого рубежа'
        },
        {
          year: '2015',
          title: 'Международное расширение',
          description: 'Вышли на глобальный рынок'
        },
        {
          year: '2020',
          title: 'Цифровая трансформация',
          description: 'Запуск современной торговой платформы'
        },
        {
          year: '2024',
          title: '500,000+ клиентов',
          description: 'Полумиллионная клиентская база'
        }
      ],

      successStories: {
        title: 'Истории успеха наших клиентов',
        subtitle: 'Реальные люди, реальные результаты',
        stories: [
          {
            name: 'Александр К.',
            experience: `5 лет с ${brandName}`,
            result: 'Стабильная прибыль 15% в месяц',
            quote: `${brandName} помог мне превратить торговлю в надежный источник дохода. Профессиональная поддержка на каждом шагу.`
          },
          {
            name: 'Мария С.',
            experience: `3 года с ${brandName}`,
            result: 'Увеличила капитал в 4 раза',
            quote: 'Начинала с минимальных знаний. Благодаря обучению и поддержке команды добилась отличных результатов.'
          },
          {
            name: 'Дмитрий П.',
            experience: `7 лет с ${brandName}`,
            result: 'Заработал более $100,000',
            quote: `${brandName} — это не просто платформа, это целая экосистема для успешной торговли. Рекомендую всем!`
          }
        ]
      },

      whyChooseUs: {
        title: 'Почему клиенты выбирают нас годами',
        features: [
          {
            icon: Shield,
            title: 'Максимальная безопасность',
            description: 'Ваши средства под защитой банковских технологий'
          },
          {
            icon: TrendingUp,
            title: 'Стабильный доход',
            description: 'Помогаем клиентам зарабатывать без чрезмерных рисков'
          },
          {
            icon: Users,
            title: 'Персональная поддержка',
            description: '24/7 сопровождение опытными консультантами'
          },
          {
            icon: Award,
            title: 'Проверенная репутация',
            description: '18+ лет на рынке, тысячи довольных клиентов'
          }
        ]
      },

      cta: {
        title: 'Присоединяйтесь к успешным трейдерам',
        description: 'Мы поможем вам заработать хорошие деньги, минимизируя риски',
        button: 'Начать зарабатывать'
      }
    },

    en: {
      title: 'More than 18 years of successful work',
      subtitle: `${brandName} — reliable partner for your investments since 2006`,
      description: 'Over the years, we have helped tens of thousands of clients achieve financial goals through smart investments and professional trading.',
      
      milestones: [
        {
          year: '2006',
          title: 'Company Foundation',
          description: 'Beginning of the journey in financial technologies'
        },
        {
          year: '2010',
          title: '10,000+ clients',
          description: 'Reached our first significant milestone'
        },
        {
          year: '2015',
          title: 'International Expansion',
          description: 'Entered the global market'
        },
        {
          year: '2020',
          title: 'Digital Transformation',
          description: 'Launch of modern trading platform'
        },
        {
          year: '2024',
          title: '500,000+ clients',
          description: 'Half million client base'
        }
      ],

      successStories: {
        title: 'Success stories of our clients',
        subtitle: 'Real people, real results',
        stories: [
          {
            name: 'Alexander K.',
            experience: `5 years with ${brandName}`,
            result: 'Stable profit 15% per month',
            quote: `${brandName} helped me turn trading into a reliable source of income. Professional support at every step.`
          },
          {
            name: 'Maria S.',
            experience: `3 years with ${brandName}`,
            result: 'Increased capital 4 times',
            quote: 'Started with minimal knowledge. Thanks to training and team support achieved excellent results.'
          },
          {
            name: 'Dmitry P.',
            experience: `7 years with ${brandName}`,
            result: 'Earned more than $100,000',
            quote: `${brandName} is not just a platform, it is a whole ecosystem for successful trading. Recommend to everyone!`
          }
        ]
      },

      whyChooseUs: {
        title: 'Why clients choose us for years',
        features: [
          {
            icon: Shield,
            title: 'Maximum security',
            description: 'Your funds protected by banking technologies'
          },
          {
            icon: TrendingUp,
            title: 'Stable income',
            description: 'Help clients earn without excessive risks'
          },
          {
            icon: Users,
            title: 'Personal support',
            description: '24/7 support from experienced consultants'
          },
          {
            icon: Award,
            title: 'Proven reputation',
            description: '18+ years on the market, thousands of satisfied clients'
          }
        ]
      },

      cta: {
        title: 'Join successful traders',
        description: 'We will help you earn good money while minimizing risks',
        button: 'Start earning'
      }
    },

    kk: {
      title: '18 жылдан астам табысты жұмыс',
      subtitle: `${brandName} — 2006 жылдан бері инвестицияларыңыз үшін сенімді серіктес`,
      description: 'Жұмыс жылдары бойы біз ақылды инвестициялар мен кәсіби сауда арқылы он мыңдаған клиенттерге қаржылық мақсаттарына жетуге көмектестік.',
      
      milestones: [
        {
          year: '2006',
          title: 'Компанияны құру',
          description: 'Қаржылық технологиялардағы саяхаттың басталуы'
        },
        {
          year: '2010',
          title: '10,000+ клиент',
          description: 'Алғашқы маңызды жетістікке жеттік'
        },
        {
          year: '2015',
          title: 'Халықаралық кеңею',
          description: 'Жаһандық нарыққа шықтық'
        },
        {
          year: '2020',
          title: 'Цифрлық трансформация',
          description: 'Заманауи сауда платформасын іске қосу'
        },
        {
          year: '2024',
          title: '500,000+ клиент',
          description: 'Жарты миллионды клиент базасы'
        }
      ],

      successStories: {
        title: 'Клиенттерімізд табыс тарихтары',
        subtitle: 'Нақты адамдар, нақты нәтижелер',
        stories: [
          {
            name: 'Александр К.',
            experience: `${brandName}-пен 5 жыл`,
            result: 'Тұрақты пайда айына 15%',
            quote: `${brandName} маған сауданы сенімді табыс көзіне айналдыруға көмектесті. Әр қадамда кәсіби қолдау.`
          },
          {
            name: 'Мария С.',
            experience: `${brandName}-пен 3 жыл`,
            result: 'Капиталды 4 есе арттырды',
            quote: 'Минималды білімнен бастадым. Команданың оқытуы мен қолдауының арқасында тамаша нәтижелерге қол жеткіздім.'
          },
          {
            name: 'Дмитрий П.',
            experience: `${brandName}-пен 7 жыл`,
            result: '$100,000-нан астам тапты',
            quote: `${brandName} - бұл жай платформа емес, бұл табысты сауда үшін толық экожүйе. Барлығына ұсынамын!`
          }
        ]
      },

      whyChooseUs: {
        title: 'Неліктен клиенттер бізді жылдар бойы таңдайды',
        features: [
          {
            icon: Shield,
            title: 'Максималды қауіпсіздік',
            description: 'Қаражатыңыз банктік технологиялардың қорғанысында'
          },
          {
            icon: TrendingUp,
            title: 'Тұрақты табыс',
            description: 'Клиенттерге шамадан тыс тәуекелсіз табыс табуға көмектесеміз'
          },
          {
            icon: Users,
            title: 'Жеке қолдау',
            description: 'Тәжірибелі кеңесшілермен 24/7 қолдау'
          },
          {
            icon: Award,
            title: 'Дәлелденген беделі',
            description: 'Нарықта 18+ жыл, мыңдаған риза клиенттер'
          }
        ]
      },

      cta: {
        title: 'Табысты трейдерлерге қосылыңыз',
        description: 'Біз сізге тәуекелдерді азайта отырып жақсы ақша табуға көмектесеміз',
        button: 'Табыс табуды бастау'
      }
    }
  };

  const t = translations[language];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-emerald-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-emerald-600 font-semibold mb-4">
            {t.subtitle}
          </p>
          <p className="text-gray-600 max-w-3xl mx-auto text-lg">
            {t.description}
          </p>
        </div>

        {/* Timeline */}
        <div className="mb-20">
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-emerald-600 to-green-600 rounded-full"></div>
            
            <div className="space-y-12">
              {t.milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                      <div className="text-3xl font-bold text-emerald-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="w-4 h-4 bg-emerald-600 rounded-full border-4 border-white shadow-lg z-10"></div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Success Stories */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              {t.successStories.title}
            </h3>
            <p className="text-xl text-gray-600">
              {t.successStories.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.successStories.stories.map((story, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {story.name.charAt(0)}
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900">{story.name}</h4>
                    <p className="text-sm text-gray-600">{story.experience}</p>
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-3 mb-4">
                  <div className="text-green-800 font-semibold text-center">
                    {story.result}
                  </div>
                </div>
                
                <blockquote className="text-gray-700 italic">
                  "{story.quote}"
                </blockquote>
                
                <div className="flex items-center mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            {t.whyChooseUs.title}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.whyChooseUs.features.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h4>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.cta.title}
          </h3>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            {t.cta.description}
          </p>
          <button 
            onClick={onRegister}
            className="px-8 py-4 bg-white text-emerald-600 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl">
            {t.cta.button}
            <ArrowRight className="w-5 h-5 ml-2 inline" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutSection;